// src/components/CanvasTimelineView.js

import React, { useState, useMemo } from 'react';
import { Box, Tooltip, Typography, Fade } from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { getGroupColor } from '../utils/colorPalette';

/**
 * CanvasTimelineView Component
 *
 * Displays files as a timeline organized by canvas groups:
 * - Each canvas = horizontal row
 * - Snapshots (.suprs) = large circles (60px)
 * - Deltas (.supr) = small circles (20px)
 * - Color-coded by canvas group
 * - Files sorted chronologically (left to right)
 *
 * Props:
 * - files: Array of file objects to display
 * - selectedFiles: Array of selected file keys
 * - onFileSelect: Handler for file selection (fileKey, isCtrlClick)
 * - onFileClick: Handler for file preview/open
 * - groupColors: Map of canvas group colors
 * - bucketType: Current bucket type (for color theming)
 */
const CanvasTimelineView = ({
  files = [],
  selectedFiles = [],
  onFileSelect,
  onFileClick,
  onSelectAll,
  groupColors,
  bucketType = 'live',
  selectedGroup = 'all'
}) => {
  const [hoveredFile, setHoveredFile] = useState(null);

  // Group files by canvas and sort chronologically
  const canvasGroups = useMemo(() => {
    // Live bucket: NO grouping, just sort by timestamp (newest first)
    if (bucketType === 'live') {
      const sortedFiles = [...files].sort((a, b) => {
        const timeA = parseInt(a.metadata?.timestamp || 0);
        const timeB = parseInt(b.metadata?.timestamp || 0);
        return timeB - timeA; // Reversed: newest first
      });

      // Return as single "group" with no canvas name
      return [{
        name: null,
        files: sortedFiles,
        color: null
      }];
    }

    // Profile bucket: Group by canvas name
    const groups = {};
    files.forEach(file => {
      const canvasName = file.metadata?.['document-name'] || 'Untitled';
      if (!groups[canvasName]) {
        groups[canvasName] = [];
      }
      groups[canvasName].push(file);
    });

    // Sort files within each group by timestamp (newest first)
    Object.keys(groups).forEach(canvasName => {
      groups[canvasName].sort((a, b) => {
        const timeA = parseInt(a.metadata?.timestamp || 0);
        const timeB = parseInt(b.metadata?.timestamp || 0);
        return timeB - timeA; // Reversed: newest first
      });
    });

    // Convert to array format for rendering
    return Object.keys(groups).map(canvasName => ({
      name: canvasName,
      files: groups[canvasName],
      color: groupColors?.[canvasName] || getGroupColor(canvasName)
    }));
  }, [files, groupColors, bucketType]);

  // Helper function to get file extension from file name
  const getExtension = (file) => {
    // Try file.extension first
    let extension = file.extension?.toLowerCase() || '';

    // If empty, extract from file name
    if (!extension && file.name) {
      const nameParts = file.name.split('.');
      if (nameParts.length > 1) {
        extension = '.' + nameParts[nameParts.length - 1].toLowerCase();
      }
    } else if (extension && !extension.startsWith('.')) {
      // Ensure extension starts with a dot
      extension = '.' + extension;
    }

    return extension;
  };

  // Get size based on file type
  const getFileSize = (file) => {
    const extension = getExtension(file);

    // Snapshots = squares (28px)
    if (extension === '.suprs' || extension === '.json') {
      return 28;
    }

    // Deltas/Strokes = small squares (15px - 3/4ths of original 20px)
    if (extension === '.supr') {
      return 15;
    }

    // Other files = medium (24px)
    return 24;
  };

  // Get shape based on file type
  const getFileShape = (file) => {
    const extension = getExtension(file);

    // Debug logging
    console.log('File:', file.name, 'Extension:', extension, 'Shape:', 'square');

    // All files are now squares (snapshots and deltas)
    return 'square';
  };

  // Handle file click
  const handleFileClick = (file, event) => {
    event.stopPropagation(); // Prevent event bubbling
    const isCtrlClick = event.ctrlKey || event.metaKey;
    const isShiftClick = event.shiftKey;

    // Use file.path as the unique identifier (file.key is undefined)
    const fileIdentifier = file.path || file.key || file.name;
    console.log('Clicked file:', file.name, 'Identifier:', fileIdentifier, 'Ctrl:', isCtrlClick, 'Shift:', isShiftClick);

    if (onFileSelect) {
      onFileSelect(fileIdentifier, isCtrlClick, isShiftClick);
    }
  };

  // Handle file double-click (preview)
  const handleFileDoubleClick = (file, event) => {
    event.preventDefault();
    event.stopPropagation(); // Prevent event bubbling
    if (onFileClick) {
      onFileClick(file);
    }
  };

  // Check if file is selected
  const isFileSelected = (file) => {
    const fileIdentifier = file.path || file.key || file.name;
    const selected = selectedFiles.some(
      selectedFile => (selectedFile.path || selectedFile.key || selectedFile.name) === fileIdentifier
    );
    return selected;
  };

  // Format file size for display
  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
  };

  // Format timestamp for display
  const formatTimestamp = (file) => {
    if (!file.metadata?.timestamp) return 'Unknown date';
    const date = new Date(parseInt(file.metadata.timestamp));
    return date.toLocaleString();
  };

  // Get file type label
  const getFileTypeLabel = (file) => {
    const extension = getExtension(file);
    if (extension === '.suprs') return 'Snapshot';
    if (extension === '.supr') return 'Stroke';
    if (extension === '.json') return 'Snapshot (Legacy)';
    return extension || 'Unknown';
  };

  // If no files, show empty state
  if (files.length === 0) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
          gap: 2
        }}
      >
        <Typography variant="h6" color="text.secondary">
          No files to display
        </Typography>
        <Typography variant="body2" color="text.disabled">
          Upload files or switch buckets to view timeline
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'background.default',
        borderRadius: 1,
      }}
    >
      {/* Header Bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 2,
          paddingBottom: 1.5,
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          backgroundColor: 'background.paper',
        }}
      >
        {/* Left side - Context info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography
            variant="subtitle2"
            sx={{
              color: 'text.secondary',
              fontWeight: 600,
              textTransform: 'uppercase',
              fontSize: '0.75rem',
              letterSpacing: 0.5,
            }}
          >
            {bucketType === 'live' ? (
              selectedGroup === 'all' ? 'All Files' : selectedGroup
            ) : (
              'Canvas Timeline'
            )}
          </Typography>

          <Typography
            variant="caption"
            sx={{
              color: 'text.disabled',
              fontSize: '0.7rem',
            }}
          >
            •
          </Typography>

          <Typography
            variant="caption"
            sx={{
              color: 'text.disabled',
              fontSize: '0.7rem',
            }}
          >
            {bucketType === 'profile'
              ? `${canvasGroups.length} canvas group${canvasGroups.length !== 1 ? 's' : ''}`
              : ''
            }
            {bucketType === 'profile' && ' • '}
            {files.length} file{files.length !== 1 ? 's' : ''}
            {selectedFiles.length > 0 && ` • ${selectedFiles.length} selected`}
          </Typography>
        </Box>

        {/* Center - Legend (hidden on mobile) */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            gap: 2,
            paddingX: 2,
          }}
        >
          {/* Snapshot Legend */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box
              sx={{
                width: '28px',
                height: '28px',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '6px',
                flexShrink: 0,
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: '0.7rem',
                fontWeight: 500,
              }}
            >
              Snapshot
            </Typography>
          </Box>

          {/* Stroke Legend */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box
              sx={{
                width: '15px',
                height: '15px',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '6px',
                flexShrink: 0,
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: '0.7rem',
                fontWeight: 500,
              }}
            >
              Stroke
            </Typography>
          </Box>
        </Box>

        {/* Right side - Select All button */}
        <Box>
          {/* Select All toggle */}
          {files.length > 0 && onSelectAll && (
            <Box
              onClick={() => {
                // Check if all files are selected
                const allSelected = files.every(file => isFileSelected(file));

                // Call onSelectAll with a synthetic event
                onSelectAll({ target: { checked: !allSelected } });
              }}
              sx={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                padding: '4px 12px',
                borderRadius: 1,
                border: '1px solid rgba(255, 255, 255, 0.2)',
                backgroundColor: 'rgba(255, 255, 255, 0.02)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                }
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  userSelect: 'none',
                }}
              >
                {(() => {
                  const allSelected = files.every(file => isFileSelected(file));
                  return allSelected ? 'Deselect All' : 'Select All';
                })()}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Timeline Content */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          padding: 2,
        }}
      >
        {/* Canvas Groups */}
        {canvasGroups.map((group, groupIndex) => (
        <Box
          key={group.name}
          sx={{
            marginBottom: 3,
            paddingBottom: 2,
            borderBottom: groupIndex < canvasGroups.length - 1 ? '1px solid rgba(255, 255, 255, 0.1)' : 'none'
          }}
        >
          {/* Canvas Label (only show for Profile bucket) */}
          {group.name && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: 2,
                gap: 1.5
              }}
            >
              {/* Color indicator */}
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  backgroundColor: group.color,
                  flexShrink: 0
                }}
              />

              {/* Canvas name */}
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary'
                }}
              >
                {group.name}
              </Typography>

              {/* File count */}
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  marginLeft: 'auto'
                }}
              >
                {group.files.length} file{group.files.length !== 1 ? 's' : ''}
              </Typography>
            </Box>
          )}

          {/* Timeline Row */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
              paddingLeft: group.name ? 4 : 0
            }}
          >
            {group.files.map((file, fileIndex) => {
              const size = getFileSize(file);
              const shape = getFileShape(file);
              const fileIdentifier = file.path || file.key || file.name;
              const isSelected = isFileSelected(file);
              const isHovered = hoveredFile === fileIdentifier;

              // Get color for each file (important for Live bucket where each file can be different canvas)
              const fileColor = bucketType === 'live'
                ? (groupColors?.[file.metadata?.['document-name']] || getGroupColor(file.metadata?.['document-name'] || 'Untitled'))
                : group.color;

              return (
                <Tooltip
                  key={fileIdentifier || fileIndex}
                  title={
                    <Box sx={{ p: 0.5 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {file.name}
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                        Type: {getFileTypeLabel(file)}
                      </Typography>
                      <Typography variant="caption" display="block">
                        Size: {formatSize(file.size || 0)}
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        {formatTimestamp(file)}
                      </Typography>
                    </Box>
                  }
                  arrow
                  placement="top"
                  enterDelay={300}
                  disableInteractive
                  TransitionComponent={Fade}
                  TransitionProps={{ timeout: 200 }}
                  PopperProps={{
                    sx: {
                      pointerEvents: 'none'
                    }
                  }}
                >
                  <Box
                    sx={{
                      width: size,
                      height: size,
                      borderRadius: shape === 'circle' ? '50%' : '6px',
                      backgroundColor: fileColor,
                      cursor: 'pointer',
                      position: 'relative',
                      transition: 'all 0.2s ease',
                      border: isSelected
                        ? '3px solid rgba(255, 255, 255, 0.9)'
                        : 'none',
                      boxShadow: isHovered
                        ? '0 4px 12px rgba(0, 0, 0, 0.5)'
                        : '0 1px 3px rgba(0, 0, 0, 0.2)',
                      transform: isHovered ? 'scale(1.1)' : 'scale(1)',
                      opacity: isSelected ? 1 : 0.85,
                      '&:hover': {
                        opacity: 1
                      }
                    }}
                    onMouseEnter={() => setHoveredFile(fileIdentifier)}
                    onMouseLeave={() => setHoveredFile(null)}
                    onClick={(e) => handleFileClick(file, e)}
                    onDoubleClick={(e) => handleFileDoubleClick(file, e)}
                  >
                    {/* Selection indicator */}
                    {isSelected && size >= 24 && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          pointerEvents: 'none'
                        }}
                      >
                        <CheckCircleIcon
                          sx={{
                            fontSize: size * 0.5,
                            color: '#ffffff',
                            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))'
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      ))}
      </Box>
    </Box>
  );
};

// Export with React.memo for performance
export default React.memo(CanvasTimelineView);
